// i18n — Korean + English, persisted. API mirrors the reference games'
// i18n.js exactly: LANGUAGES / t(key, vars) / getLanguage / setLanguage /
// useT (via useSyncExternalStore) so components and future locales are drop-in.
// Point amounts live in code and are interpolated in, so policy text can never
// drift from what is charged.

import { useSyncExternalStore } from "react";
import { key } from "../gameId.ts";

export const LANGUAGES = [
  { code: "ko", label: "한국어" },
  { code: "en", label: "English" },
] as const;

export type LangCode = (typeof LANGUAGES)[number]["code"];

type Table = Record<string, string>;

const STRINGS: Record<LangCode, Table> = {
  ko: {
    // game title/subtitle come from src/app/gameConfig.ts, not here.
    play: "게임 시작",
    playAgain: "다시 도전",
    restart: "다시 시작",
    resume: "계속하기",
    options: "옵션",
    account: "계정",
    close: "닫기",
    back: "뒤로",
    cancel: "취소",

    points: "포인트",
    pointPolicy: "포인트 정책",
    welcomePoints: "처음 접속하면 {points}P를 받습니다.",
    guestPlay: "게스트도 플레이·포인트 적립/사용이 가능합니다. 로그인은 클라우드 동기화 수단입니다.",
    cloudPreferred: "로그인하면 클라우드 잔액이 로컬보다 우선하며 기기 간 동기화됩니다.",
    lowPointsGuard: "포인트가 부족한 유료 행동은 실행 전에 비활성화됩니다.",

    adArea: "광고 영역",
    signInCta: "로그인하기",
    loginUnconfigured: "로그인 미설정",
    settings: "설정",
    language: "언어",
    sound: "사운드 재생",
    reducedMotion: "모션 줄이기",
    privacy: "개인정보처리방침",
    pointsSyncAccount: "포인트가 이 계정에 저장돼 기기 간 동기화됩니다.",
    pointsSyncSignIn: "로그인하면 포인트가 계정에 저장돼 기기 간 동기화됩니다.",
    login: "로그인",
    logout: "로그아웃",
    signedInAs: "로그인됨",
    authSetupNeeded: "로그인 기능이 준비되면 포인트를 계정에 저장할 수 있습니다.",
    password: "비밀번호",
    firebaseUnconfigured: "로그인 서버가 아직 설정되지 않았습니다.",
    authError: "인증 오류가 발생했습니다.",
    loginFailed: "인증 오류가 발생했습니다.",

    emailAuthLoginTitle: "게임 계정 로그인",
    emailAuthCreateTitle: "게임 계정 만들기",
    emailAuthSubtitle: "이메일 계정은 웹과 (추후) Android에서 함께 사용할 수 있습니다.",
    emailAuthEmail: "이메일",
    emailAuthPassword: "비밀번호",
    emailAuthPasswordHint: "(게임 전용 · 6자 이상)",
    emailAuthPasswordConfirm: "비밀번호 확인",
    emailAuthNickname: "닉네임 (선택)",
    emailAuthLogin: "로그인",
    emailAuthCreate: "계정 만들기",
    emailAuthWorking: "처리 중...",
    emailAuthNeedAccount: "처음이신가요? 계정 만들기",
    emailAuthHaveAccount: "이미 계정이 있나요? 로그인",
    emailAuthForgotPassword: "비밀번호 재설정 메일 보내기",
    emailAuthResend: "인증메일 다시 보내기",
    emailAuthEnterEmail: "이메일을 입력해 주세요.",
    emailAuthEnterPassword: "비밀번호를 입력해 주세요.",
    emailAuthVerificationSent: "인증메일을 보냈습니다. 메일이 보이지 않으면 스팸메일함도 확인한 뒤, 인증 후 로그인해 주세요.",
    emailAuthVerificationResent: "인증메일을 다시 보냈습니다. 메일이 보이지 않으면 스팸메일함도 확인해 주세요.",
    emailAuthResetSent: "비밀번호 재설정 메일을 보냈습니다.",
    emailAuthNotVerified: "이메일 인증이 필요합니다.",
    emailAuthAlreadyUsed: "이미 사용 중인 이메일입니다.",
    emailAuthInvalidEmail: "올바른 이메일을 입력해 주세요.",
    emailAuthWeakPassword: "비밀번호는 6자 이상이어야 합니다.",
    emailAuthPasswordMismatch: "비밀번호가 서로 일치하지 않습니다.",
    emailAuthInvalidCredential: "이메일 또는 비밀번호가 올바르지 않습니다.",
    emailAuthTooManyRequests: "요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.",
    googleAuthOr: "또는",
    googleAuthContinue: "Google 계정으로 계속하기",
    googleAuthAndroidOnly: "Google 로그인은 Android 앱에서 사용할 수 있습니다.",
    googleAuthNotVerified: "Google 계정의 이메일 인증을 확인할 수 없습니다.",

    deleteAccount: "계정 삭제",
    deleteConfirm: "이 계정과 클라우드에 저장된 데이터를 영구히 삭제합니다. 삭제 후에는 복구할 수 없습니다.",
    deleteAgree: "위 계정과 모든 연동 데이터를 영구히 삭제하는 데 동의합니다.",
    deleteDoneTitle: "삭제 완료",
    deleteDoneBody: "계정과 연동 데이터가 삭제되었습니다. 이용해 주셔서 감사합니다.",

    updateNeeded: "업데이트가 필요합니다",
    updateBodyWeb: "새 버전이 배포되었습니다. 아래 버튼을 눌러 최신 버전으로 새로고침해 주세요.",
    currentVersion: "현재 버전",
    latestVersion: "최신 버전",
    retryUpdate: "지금 업데이트",
    devBuild: "개발 빌드",

    // 친구랑 같이하기 (ported from Coin-puzzle's net/peer.js + ui/Lobby.jsx)
    versus: "친구와 1:1 대결 ({points}P)",
    notEnoughPoints: "포인트가 부족합니다 ({points}P 필요)",
    hostGame: "방 만들기",
    joinGame: "방 참여하기",
    roomCode: "방 코드",
    enterRoomCode: "방 코드 입력",
    connecting: "연결 중...",
    waitingForGuest: "상대를 기다리는 중...",
    connected: "연결됨",
    connectFailed: "연결에 실패했습니다.",
    lobbySub: "같은 Wi-Fi로 1:1 대결을 연결합니다.",
    or: "또는",
    waitingHostResponse: "호스트 응답 대기 중...",
    youWin: "승리!",
    youLose: "패배",
    rematch: "다시하기",
    rematchCost: "다시하기 {points}P",
    waitOpponent: "상대 대기중",
    waitRematchRequest: "상대의 재대결 신청을 기다리는 중",
    rematchNoPoints: "포인트가 부족합니다. 충전 후 다시 도전하세요.",
    exit: "나가기",
    exitConfirm: "게임을 종료할까요?",
  },
  en: {
    // game title/subtitle come from src/app/gameConfig.ts, not here.
    play: "Play",
    playAgain: "Play again",
    restart: "Restart",
    resume: "Resume",
    options: "Options",
    account: "Account",
    close: "Close",
    back: "Back",
    cancel: "Cancel",

    points: "Points",
    pointPolicy: "Point policy",
    welcomePoints: "First visit grants a {points}P welcome bonus.",
    guestPlay: "Guests can play and earn/spend points. Sign-in is only for cloud sync.",
    cloudPreferred: "After sign-in the cloud balance wins over local and syncs across devices.",
    lowPointsGuard: "Paid actions are disabled before they run when points are short.",

    adArea: "Ad area",
    signInCta: "Sign in",
    loginUnconfigured: "Sign-in unavailable",
    settings: "Settings",
    language: "Language",
    sound: "Sound",
    reducedMotion: "Reduce motion",
    privacy: "Privacy policy",
    pointsSyncAccount: "Points are saved to this account and synced across devices.",
    pointsSyncSignIn: "Sign in to save points to your account and sync across devices.",
    login: "Sign in",
    logout: "Sign out",
    signedInAs: "Signed in",
    authSetupNeeded: "Once sign-in is ready, points can be saved to your account.",
    password: "Password",
    firebaseUnconfigured: "The sign-in server is not configured yet.",
    authError: "An authentication error occurred.",
    loginFailed: "An authentication error occurred.",

    emailAuthLoginTitle: "Sign in to your game account",
    emailAuthCreateTitle: "Create a game account",
    emailAuthSubtitle: "Use the same email account on the web and (later) Android.",
    emailAuthEmail: "Email",
    emailAuthPassword: "Password",
    emailAuthPasswordHint: "(game only · 6+ chars)",
    emailAuthPasswordConfirm: "Confirm password",
    emailAuthNickname: "Nickname (optional)",
    emailAuthLogin: "Sign in",
    emailAuthCreate: "Create account",
    emailAuthWorking: "Working...",
    emailAuthNeedAccount: "New here? Create an account",
    emailAuthHaveAccount: "Already have an account? Sign in",
    emailAuthForgotPassword: "Send password reset email",
    emailAuthResend: "Resend verification email",
    emailAuthEnterEmail: "Please enter your email.",
    emailAuthEnterPassword: "Please enter your password.",
    emailAuthVerificationSent: "Verification email sent. If you don't see it, check your spam folder, then verify your address and sign in.",
    emailAuthVerificationResent: "Verification email sent again. If you don't see it, check your spam folder.",
    emailAuthResetSent: "Password reset email sent.",
    emailAuthNotVerified: "Email verification is required.",
    emailAuthAlreadyUsed: "That email is already in use.",
    emailAuthInvalidEmail: "Please enter a valid email.",
    emailAuthWeakPassword: "Password must be at least 6 characters.",
    emailAuthPasswordMismatch: "Passwords do not match.",
    emailAuthInvalidCredential: "Incorrect email or password.",
    emailAuthTooManyRequests: "Too many attempts. Try again later.",
    googleAuthOr: "or",
    googleAuthContinue: "Continue with Google",
    googleAuthAndroidOnly: "Google sign-in is available in the Android app.",
    googleAuthNotVerified: "We could not verify the Google account email address.",

    deleteAccount: "Delete account",
    deleteConfirm: "This permanently deletes this account and its cloud data. This cannot be undone.",
    deleteAgree: "I agree to permanently delete this account and all linked data.",
    deleteDoneTitle: "Deleted",
    deleteDoneBody: "Your account and linked data have been deleted. Thank you for playing.",

    updateNeeded: "Update required",
    updateBodyWeb: "A new version is live. Tap below to refresh to the latest build.",
    currentVersion: "Current",
    latestVersion: "Latest",
    retryUpdate: "Update now",
    devBuild: "dev build",

    // 친구랑 같이하기 (ported from Coin-puzzle's net/peer.js + ui/Lobby.jsx)
    versus: "1:1 with a Friend ({points}P)",
    notEnoughPoints: "Not enough points ({points}P needed)",
    hostGame: "Create room",
    joinGame: "Join room",
    roomCode: "Room code",
    enterRoomCode: "Enter room code",
    connecting: "Connecting...",
    waitingForGuest: "Waiting for opponent...",
    connected: "Connected",
    connectFailed: "Connection failed.",
    lobbySub: "Connect a 1:1 match over the same Wi-Fi.",
    or: "or",
    waitingHostResponse: "Waiting for host response...",
    youWin: "YOU WIN!",
    youLose: "YOU LOSE",
    rematch: "Rematch",
    rematchCost: "Rematch {points}P",
    waitOpponent: "Waiting for opponent",
    waitRematchRequest: "Waiting for a rematch request",
    rematchNoPoints: "Not enough points. Top up and challenge again.",
    exit: "Exit",
    exitConfirm: "Quit the game?",
  },
};

const LANG_KEY = key("lang");

function initialLocale(): LangCode {
  try {
    const saved = localStorage.getItem(LANG_KEY) as LangCode | null;
    if (saved && STRINGS[saved]) return saved;
  } catch {
    /* ignore */
  }
  return typeof navigator !== "undefined" && /^ko/i.test(navigator.language || "")
    ? "ko"
    : "en";
}

let locale: LangCode = initialLocale();
const listeners = new Set<() => void>();

export function t(key: string, vars: Record<string, string | number> = {}): string {
  const template = STRINGS[locale]?.[key] ?? STRINGS.ko[key] ?? key;
  return template.replace(/\{(\w+)\}/g, (_, name) => `${vars[name] ?? ""}`);
}

export function getLanguage(): LangCode {
  return locale;
}

export function setLanguage(l: LangCode): void {
  if (!STRINGS[l] || l === locale) return;
  locale = l;
  try {
    localStorage.setItem(LANG_KEY, l);
  } catch {
    /* ignore */
  }
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function getSnapshot() {
  return locale;
}

/** React hook: returns t() bound to the current locale (re-renders on change). */
export function useT(): typeof t {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return t;
}

/** React hook: the current locale itself (re-renders on change). For screens
 *  that need to pick between bilingual data (e.g. campaign story text) rather
 *  than a t() lookup. */
export function useLang(): LangCode {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
